import { isDraftable, produce } from 'immer';

import { IRedactionRule } from './types';

/**
 * Get default redaction rules
 * These rules match common sensitive data patterns
 */
export function getDefaultRedactionRules(): IRedactionRule[] {
	return [
		{
			/** JWT tokens (Bearer [token]) */
			patterns: [
				/Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/gi,
			],
			replacement: 'Bearer [REDACTED-JWT]',
		},
	];
}

/**
 * Log redactor engine
 * Automatically redacts sensitive data from logs
 */
export class LogRedactor {
	private rules: IRedactionRule[];
	private sensitiveKeys: string[];

	constructor(customRules?: IRedactionRule[]) {
		this.rules = [...getDefaultRedactionRules(), ...(customRules || [])];
		this.sensitiveKeys = [
			'password',
			'token',
			'secret',
			'apikey',
			'creditcard',
			'cvv',
			'pin',
			'authorization',
		];
	}

	/**
	 * Redact sensitive data from any value
	 * @param data - Data to redact (can be object, array, string, or primitive)
	 * @returns Immutable redacted copy of the data
	 */
	public redact<T>(data: T): T {
		if (data === null || data === undefined) {
			return data;
		}

		/** Handle non-object primitives */
		if (typeof data !== 'object') {
			return this.redactString(data) as T;
		}

		/** Handle Error objects separately */
		if (data instanceof Error) {
			return this.redactError(data) as T;
		}

		/** Skip non-draftable objects (Date, RegExp, class instances, etc.) */
		if (!isDraftable(data)) {
			return data;
		}

		/** Handle arrays with Immer */
		if (Array.isArray(data)) {
			return produce(data, (draft) => {
				for (let i = 0; i < draft.length; i++) {
					draft[i] = this.redact(draft[i]);
				}
			}) as T;
		}

		/** Handle plain objects with Immer */
		return produce(data as Record<string, unknown>, (draft) => {
			for (const key in draft) {
				if (this.shouldRedactKey(key)) {
					draft[key] = '[REDACTED]';
				} else if (typeof draft[key] === 'object' && draft[key] !== null) {
					draft[key] = this.redact(draft[key]);
				} else {
					draft[key] = this.redactString(draft[key]);
				}
			}
		}) as T;
	}

	/**
	 * Redact sensitive data from Error object
	 * @param error - Error to redact
	 * @returns New Error with redacted message
	 */
	private redactError(error: Error): Error {
		const redactedMessage = this.redactString(error.message);
		const redactedError = new Error(redactedMessage);
		redactedError.name = error.name;
		redactedError.stack = error.stack
			? this.redactString(error.stack)
			: undefined;
		return redactedError;
	}

	/**
	 * Check if a key should be redacted based on sensitive keywords
	 * @param key - Object key to check
	 * @returns True if key should be redacted
	 */
	private shouldRedactKey(key: string): boolean {
		const lowerKey = key.toLowerCase();
		return this.sensitiveKeys.some((k) => lowerKey.includes(k));
	}

	/**
	 * Redact sensitive patterns from string values
	 * @param value - Value to redact
	 * @returns Redacted value (unchanged if not a string)
	 */
	private redactString<T>(value: T): T | string {
		if (typeof value !== 'string') {
			return value;
		}

		let result: string = value;

		for (const rule of this.rules) {
			if (rule.patterns) {
				for (const pattern of rule.patterns) {
					result = result.replace(pattern, rule.replacement || '[REDACTED]');
				}
			}
		}

		return result;
	}
}
